import * as React from 'react';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from './config/axios';

/**
 * Interface for location edit form data structure
 */
interface LocationEditFormData {
  name: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  radius: number;
  latitude: string;
  longitude: string;
}

/**
 * Interface for location object structure (API response)
 */
interface Location {
  id: number;
  name: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  radius: number;
  latitude?: string;
  longitude?: string;
  [key: string]: any; // Allow for additional properties that might come from the API
}

function LocationEdit(): React.JSX.Element {
  const { id } = useParams<{ id: string }>(); // Holt :id aus der URL
  const navigate = useNavigate();

  // State für das Formular
  const [formData, setFormData] = useState<LocationEditFormData>({
    name: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
    radius: 50,
    latitude: '',
    longitude: '',
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // Beim Mount: Lade den existierenden Location-Datensatz
  useEffect(() => {
    if (!id) return;

    axiosInstance
      .get<Location>(`/api/locations/${id}/`)
      .then((res) => {
        setFormData({
          name: res.data.name || '',
          street: res.data.street || '',
          house_number: res.data.house_number || '',
          postal_code: res.data.postal_code || '',
          city: res.data.city || '',
          radius: res.data.radius || 50,
          latitude: res.data.latitude || '',   // read-only
          longitude: res.data.longitude || '', // read-only
        });
        setLoading(false);
      })
      .catch((err: any) => {
        console.error('Error loading Location detail:', err);
        setError('Could not load Location data');
        setLoading(false);
      });
  }, [id]);

  // Änderungen im Formular
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === 'number' ? Number(value) : value 
    }));
  };

  // Speichern / Updaten
  const handleUpdate = (): void => {
    if (!id) return;
    
    setError('');
    // Patch an /api/locations/:id/
    axiosInstance
      .patch<Location>(`/api/locations/${id}/`, formData)
      .then(() => {
        navigate('/locations');
      })
      .catch((err: any) => {
        console.error('Error updating Location:', err);
        setError('Failed to update location. Check console for details.');
      });
  };

  const handleCancel = (): void => {
    navigate('/locations');
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading location data...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>Edit Location #{id}</h2>

      {error && (
        <div
          style={{
            marginBottom: '15px',
            color: 'red',
            border: '1px solid red',
            padding: '8px',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ marginBottom: '10px' }}>
        <label>Name:</label>
        <br />
        <input
          name="name"
          value={formData.name}
          onChange={handleChange}
          style={{ width: '300px' }}
        />
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label>Street:</label>
        <br />
        <input
          name="street"
          value={formData.street}
          onChange={handleChange}
          style={{ width: '300px' }}
        />
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label>House number:</label>
        <br />
        <input
          name="house_number"
          value={formData.house_number}
          onChange={handleChange}
          style={{ width: '300px' }}
        />
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label>Postal Code:</label>
        <br />
        <input
          name="postal_code"
          value={formData.postal_code}
          onChange={handleChange}
          style={{ width: '300px' }}
        />
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label>City:</label>
        <br />
        <input
          name="city"
          value={formData.city}
          onChange={handleChange}
          style={{ width: '300px' }}
        />
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label>Radius (m):</label>
        <br />
        <input
          type="number"
          name="radius"
          value={formData.radius}
          onChange={handleChange}
          style={{ width: '100px' }}
        />
      </div>

      {/* read-only lat/lon => Admin sieht, was Geocoder geliefert hat */}
      <div style={{ marginBottom: '10px' }}>
        <label>Latitude (read-only):</label>
        <br />
        <input
          name="latitude"
          value={formData.latitude}
          readOnly
          style={{ width: '300px', backgroundColor: '#eee' }}
        />
      </div>
      <div style={{ marginBottom: '10px' }}>
        <label>Longitude (read-only):</label>
        <br />
        <input
          name="longitude"
          value={formData.longitude}
          readOnly
          style={{ width: '300px', backgroundColor: '#eee' }}
        />
      </div>

      <button
        onClick={handleUpdate}
        style={{
          backgroundColor: '#002b80',
          color: '#fff',
          border: 'none',
          padding: '8px 12px',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        Update
      </button>

      <button
        onClick={handleCancel}
        style={{
          marginLeft: '10px',
          backgroundColor: '#6c757d',
          color: '#fff',
          border: 'none',
          padding: '8px 12px',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        Cancel
      </button>
    </div>
  );
}

export default LocationEdit; 